import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import LocationCurrencyPrompt from '../../components/ui/LocationCurrencyPrompt';
import {
  CURRENCIES,
  CURRENCY_CODES,
  FALLBACK_RATES,
  convertAmount,
  formatMoney,
  getCachedRates,
  getStoredCurrency,
  getUsdToUgxRate,
  isManualCurrency,
  cacheRates,
  storeCurrency,
  toStorageUgx,
  getGeoPromptState,
  setGeoPromptState,
  locateCountryFromCoords,
  DEFAULT_USD_TO_UGX,
} from '../../lib/currency';

const CurrencyContext = createContext(null);

function ratesUrls() {
  const urls = ['https://app.hirebroker.co/api/currency/rates'];
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      urls.unshift('http://127.0.0.1:8000/api/currency/rates');
    }
  }
  return urls;
}

export function CurrencyProvider({ children }) {
  const stored = getStoredCurrency();
  const manual = isManualCurrency();
  const [currency, setCurrencyState] = useState(() => stored || 'UGX');
  const [manualChoice, setManualChoice] = useState(manual);
  const [rates, setRates] = useState(() => getCachedRates()?.rates || FALLBACK_RATES);
  const [detected, setDetected] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(() => getCachedRates()?.updated_at || null);
  const [ready, setReady] = useState(Boolean(stored));
  const [locationStatus, setLocationStatus] = useState('idle');
  const [locationError, setLocationError] = useState(null);
  const [locationPromptVisible, setLocationPromptVisible] = useState(false);

  const usdToUgx = getUsdToUgxRate(rates);

  const applyCountryCurrency = useCallback((geo, { force = false } = {}) => {
    if (!geo) return;
    setDetected(geo);
    const code = geo.currency && CURRENCIES[geo.currency] ? geo.currency : null;
    if (!code) return;
    if (isManualCurrency() && !force) return;
    setCurrencyState(code);
    storeCurrency(code, { manual: false });
  }, []);

  const setCurrency = useCallback((code) => {
    if (!CURRENCIES[code]) return;
    setCurrencyState(code);
    setManualChoice(true);
    storeCurrency(code, { manual: true });
    setLocationPromptVisible(false);
  }, []);

  const dismissLocationPrompt = useCallback(() => {
    setLocationPromptVisible(false);
    setLocationError(null);
    if (getGeoPromptState() !== 'allowed') {
      setGeoPromptState('dismissed');
    }
  }, []);

  const requestLocation = useCallback(
    async ({ force = false, silent = false } = {}) => {
      setLocationError(null);

      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        setLocationStatus('unavailable');
        setLocationError('This browser cannot share location. Pick a currency from the menu.');
        if (!silent) setLocationPromptVisible(true);
        return;
      }

      setLocationStatus('requesting');
      if (!silent) setLocationPromptVisible(true);

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 15000,
          maximumAge: 30 * 60 * 1000,
        });
      }).catch((err) => {
        const denied = err?.code === 1;
        setGeoPromptState(denied ? 'denied' : 'dismissed');
        setLocationStatus(denied ? 'denied' : 'error');
        setLocationError(
          denied
            ? 'Location was blocked. You can pick a currency from the menu, or allow location in your browser settings.'
            : 'We could not read your location. Pick a currency from the menu, or try Allow location again.'
        );
        setLocationPromptVisible(true);
        return null;
      });

      if (!position) return;

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      const geo = await locateCountryFromCoords(lat, lng);
      if (!geo) {
        setLocationStatus('error');
        setLocationError('We found you, but could not tell which country that is. Pick a currency from the menu.');
        setLocationPromptVisible(true);
        return;
      }

      setGeoPromptState('allowed');
      setLocationStatus('granted');
      applyCountryCurrency(geo, { force });
      setManualChoice(false);
      setLocationPromptVisible(false);
    },
    [applyCountryCurrency]
  );

  const requestLocationRef = useRef(requestLocation);
  requestLocationRef.current = requestLocation;

  useEffect(() => {
    let cancelled = false;

    async function loadRates() {
      let payload = null;
      for (const url of ratesUrls()) {
        try {
          const res = await fetch(url, { headers: { Accept: 'application/json' } });
          if (res.ok) {
            payload = await res.json();
            if (payload?.rates) break;
          }
        } catch {
          payload = null;
        }
      }

      if (!payload?.rates) {
        payload = await fetchPublicRates();
      }
      if (cancelled) return;

      const nextRates = payload?.rates && payload.rates.USD
        ? { ...FALLBACK_RATES, ...payload.rates, USD: 1 }
        : FALLBACK_RATES;
      setRates(nextRates);
      cacheRates({ rates: nextRates, updated_at: payload?.updated_at, source: payload?.source });
      if (payload?.updated_at) setUpdatedAt(payload.updated_at);
      setReady(true);
    }

    async function maybeAskForLocation() {
      if (isManualCurrency()) {
        return;
      }

      const saved = getGeoPromptState();
      if (saved === 'denied' || saved === 'dismissed') {
        return;
      }

      if (saved === 'allowed') {
        await requestLocationRef.current({ force: false, silent: true });
        return;
      }

      if (cancelled) return;
      setLocationPromptVisible(true);
    }

    loadRates();
    maybeAskForLocation();

    return () => {
      cancelled = true;
    };
  }, []);

  const formatPrice = useCallback(
    (amount, sourceCurrency = 'UGX', options = {}) =>
      formatMoney(amount, {
        sourceCurrency,
        displayCurrency: currency,
        rates,
        usdToUgx,
        ...options,
      }),
    [currency, rates, usdToUgx]
  );

  const convert = useCallback(
    (amount, from, to = currency) => convertAmount(amount, from, to, rates),
    [currency, rates]
  );

  const value = useMemo(
    () => ({
      currency,
      setCurrency,
      currencies: CURRENCIES,
      currencyCodes: CURRENCY_CODES,
      usdToUgx,
      setUsdToUgx: () => {},
      defaultRate: DEFAULT_USD_TO_UGX,
      formatPrice,
      convert,
      toStorageUgx: (amount, entryCurrency) => toStorageUgx(amount, entryCurrency, rates),
      meta: CURRENCIES[currency] || CURRENCIES.UGX,
      rates,
      detected,
      updatedAt,
      ready,
      manualChoice,
      locationStatus,
      locationError,
      locationPromptVisible,
      requestLocation,
      dismissLocationPrompt,
    }),
    [
      currency,
      setCurrency,
      usdToUgx,
      formatPrice,
      convert,
      rates,
      detected,
      updatedAt,
      ready,
      manualChoice,
      locationStatus,
      locationError,
      locationPromptVisible,
      requestLocation,
      dismissLocationPrompt,
    ]
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
      <LocationCurrencyPrompt
        visible={locationPromptVisible}
        waiting={locationStatus === 'requesting'}
        error={locationError}
        onAllow={() => requestLocation({ force: true })}
        onDismiss={dismissLocationPrompt}
      />
    </CurrencyContext.Provider>
  );
}

async function fetchPublicRates() {
  const urls = [
    'https://open.er-api.com/v6/latest/USD',
    'https://api.exchangerate-api.com/v4/latest/USD',
  ];
  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (!res.ok) continue;
      const json = await res.json();
      const rates = json.rates;
      if (!rates?.USD) continue;
      return {
        rates,
        updated_at: json.time_last_update_utc || json.date,
        source: 'public',
      };
    } catch {
      /* try next */
    }
  }
  return { rates: FALLBACK_RATES, source: 'fallback' };
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    return {
      currency: 'UGX',
      setCurrency: () => {},
      currencies: CURRENCIES,
      currencyCodes: CURRENCY_CODES,
      usdToUgx: DEFAULT_USD_TO_UGX,
      setUsdToUgx: () => {},
      defaultRate: DEFAULT_USD_TO_UGX,
      formatPrice: (amount, source = 'UGX') =>
        formatMoney(amount, { sourceCurrency: source, displayCurrency: 'UGX' }),
      convert: (amount, from, to) => convertAmount(amount, from, to),
      toStorageUgx: (amount, entry) => toStorageUgx(amount, entry),
      meta: CURRENCIES.UGX,
      rates: FALLBACK_RATES,
      detected: null,
      updatedAt: null,
      ready: true,
      manualChoice: false,
      locationStatus: 'idle',
      locationError: null,
      locationPromptVisible: false,
      requestLocation: async () => {},
      dismissLocationPrompt: () => {},
    };
  }
  return ctx;
}

export default CurrencyContext;
